import {
  DeviceType,
  StreamingProtocol,
  StreamingType,
} from '@/graphql/generated/schemas'

import { CSV_UploadStatus, HeaderIndex, RawCSVResultI } from './csvParser'

export interface ParsedDeviceI {
  name?: string
  deviceType?: DeviceType
  facilityName?: string
  floorName?: string
  label?: string
  externalId?: string
  ip?: string
  macAddress?: string
  rtspPath?: string
  rtspPort?: number
  streamProtocol?: StreamingProtocol
  streamType?: StreamingType
  uploadStatus?: CSV_UploadStatus
  uploadError?: string
  floorId?: string
}

export interface CSVParseResultI {
  data: ParsedDeviceI[]
  errors: string[]
}

export const VALID_DEVICES = [
  DeviceType.Camera,
  DeviceType.Door,
  DeviceType.Alarm,
] as const

const VALID_STREAM_TYPES = [
  StreamingType.AvaCloud,
  StreamingType.AvigilonCloud,
  StreamingType.OnPremise,
  StreamingType.VerkadaCloud,
] as const

const VALID_STREAMING_PROTOCOLS = [
  StreamingProtocol.Rtsp,
  StreamingProtocol.Rtsps,
] as const

const col = (fields: string[], headerIndex: HeaderIndex, key: string): string =>
  fields[headerIndex[key]] ?? ''

const optional = (value: string | undefined): string | undefined =>
  value && value.trim() !== '' ? value : undefined

export const validateDevices = (
  rawResult: RawCSVResultI,
  facilityNameToIdMap: Map<string, string>,
  floorLookup: Map<string, string>
): CSVParseResultI => {
  const errors = [...rawResult.errors]
  const data: ParsedDeviceI[] = []

  for (const { fields, rowNumber } of rawResult.rows) {
    const { device, errors: rowErrors } = validateRow(
      fields,
      rawResult.headerIndex,
      rowNumber,
      facilityNameToIdMap,
      floorLookup
    )
    if (rowErrors.length > 0) {
      data.push({
        ...device,
        uploadStatus: CSV_UploadStatus.Failed,
        uploadError: rowErrors.join(', '),
      })
    } else {
      data.push(device)
    }
  }

  return { data, errors }
}

const validateRow = (
  fields: string[],
  headerIndex: HeaderIndex,
  rowNumber: number,
  facilityNameToIdMap: Map<string, string>,
  floorLookup: Map<string, string>
): { device?: ParsedDeviceI; errors: string[] } => {
  const errors = []
  const name = col(fields, headerIndex, 'name')
  const deviceType = col(fields, headerIndex, 'device_type')
  const facilityName = col(fields, headerIndex, 'facility_name')
  const floorName = col(fields, headerIndex, 'floor_name')
  const rtspPort = col(fields, headerIndex, 'rtsp_port')
  const streamingProtocol = col(fields, headerIndex, 'stream_protocol')
  const streamType = col(fields, headerIndex, 'stream_type')

  validateRequiredFields(
    name,
    deviceType,
    facilityName,
    floorName,
    rowNumber,
    errors
  )

  const validatedDeviceType = validateDeviceType(deviceType, rowNumber, errors)

  const floorId = validateFacilityAndFloor(
    facilityName,
    floorName,
    rowNumber,
    facilityNameToIdMap,
    floorLookup,
    errors
  )

  const base: ParsedDeviceI = {
    name,
    deviceType: validatedDeviceType,
    facilityName,
    floorName,
    floorId,
    label: optional(col(fields, headerIndex, 'label')),
    externalId: optional(col(fields, headerIndex, 'external_id')),
  }

  if (validatedDeviceType === DeviceType.Camera) {
    const {
      validatedStreamType,
      validatedStreamingProtocol,
      validatedRtspPort,
    } = validateCameraFields(
      rtspPort,
      streamingProtocol,
      streamType,
      rowNumber,
      errors
    )
    return {
      device: {
        ...base,
        ip: optional(col(fields, headerIndex, 'ip')),
        macAddress: optional(col(fields, headerIndex, 'mac_address')),
        rtspPath: optional(col(fields, headerIndex, 'rtsp_path')),
        rtspPort: validatedRtspPort || undefined,
        streamProtocol: validatedStreamingProtocol || undefined,
        streamType: validatedStreamType || undefined,
      },
      errors,
    }
  }

  return { device: base, errors }
}

const validateFacilityAndFloor = (
  facilityName: string,
  floorName: string,
  rowNumber: number,
  facilityNameToIdMap: Map<string, string>,
  floorLookup: Map<string, string>,
  errors: string[]
): string | undefined => {
  const facilityId = facilityNameToIdMap.get(facilityName)
  if (!facilityId) {
    errors.push(
      `Row ${rowNumber}: facility_name '${facilityName}' was not found`
    )
    return undefined
  }
  const floorId = floorLookup.get(`${facilityId}::${floorName}`)
  if (!floorId) {
    errors.push(
      `Row ${rowNumber}: floor_name '${floorName}' was not found in facility '${facilityName}'`
    )
    return undefined
  }
  return floorId
}

const validateRequiredFields = (
  name: string,
  deviceType: string,
  facilityName: string,
  floorName: string,
  rowNumber: number,
  errors: string[]
) => {
  if (!name) {
    errors.push(`Row ${rowNumber}: Missing required field 'name'`)
  }
  if (!deviceType) {
    errors.push(`Row ${rowNumber}: Missing required field 'device_type'`)
  }
  if (!facilityName) {
    errors.push(`Row ${rowNumber}: Missing required field 'facility_name'`)
  }
  if (!floorName) {
    errors.push(`Row ${rowNumber}: Missing required field 'floor_name'`)
  }
}

const validateDeviceType = (
  deviceType: string,
  rowNumber: number,
  errors: string[]
): DeviceType | null => {
  if (!deviceType) return null
  const deviceTypeUpper = deviceType.toUpperCase() as DeviceType
  if (!(VALID_DEVICES as readonly string[]).includes(deviceTypeUpper)) {
    errors.push(
      `Row ${rowNumber}: 'device_type' provided was ${deviceType} ` +
        `but must be one of ${VALID_DEVICES.join(', ')}`
    )
    return null
  }
  return deviceTypeUpper
}

const validateCameraFields = (
  rtspPort: string,
  streamingProtocol: string,
  streamType: string,
  rowNumber: number,
  errors: string[]
): {
  validatedStreamType?: StreamingType
  validatedStreamingProtocol?: StreamingProtocol
  validatedRtspPort?: number
} => {
  const streamTypeUpper = streamType.toUpperCase() as StreamingType
  const streamingProtocolUpper =
    streamingProtocol.toUpperCase() as StreamingProtocol
  if (
    streamType &&
    !(VALID_STREAM_TYPES as readonly string[]).includes(streamTypeUpper)
  ) {
    errors.push(
      `Row ${rowNumber}: 'stream_type' provided was ${streamType} ` +
        `but must either be empty or one of ${VALID_STREAM_TYPES.join(', ')}`
    )
  }
  if (
    streamingProtocol &&
    !(VALID_STREAMING_PROTOCOLS as readonly string[]).includes(
      streamingProtocolUpper
    )
  ) {
    errors.push(
      `Row ${rowNumber}: 'stream_protocol' provided was ${streamingProtocol} ` +
        `but must either be empty or one of ${VALID_STREAMING_PROTOCOLS.join(
          ', '
        )}`
    )
  }
  if (rtspPort && !+rtspPort) {
    errors.push(`Row ${rowNumber}: 'rtsp_port' must be a valid number`)
  }
  return {
    validatedStreamingProtocol: streamingProtocolUpper,
    validatedStreamType: streamTypeUpper,
    validatedRtspPort: +rtspPort,
  }
}
